import { Routes, Route } from 'react-router-dom'
import { Suspense } from 'react'

// Layout components
import Layout from '@/components/layout/Layout'
import AdminLayout from '@/components/layout/AdminLayout'
import ProtectedRoute from '@/components/auth/ProtectedRoute'
import LoadingSpinner from '@/components/ui/LoadingSpinner'

// Pages
import HomePage from '@/pages/HomePage'
import LoginPage from '@/pages/auth/LoginPage'
import RegisterPage from '@/pages/auth/RegisterPage'
import GigsPage from '@/pages/GigsPage'
import NotFoundPage from '@/pages/NotFoundPage'

// Real pages
import CreateGigPage from '@/pages/gigs/CreateGigPage'
import GigDetailsPage from '@/pages/gigs/GigDetailsPage'
import EditGigPage from '@/pages/gigs/EditGigPage'
import VerifyEmailPage from '@/pages/auth/VerifyEmailPage'
import ForgotPasswordPage from '@/pages/auth/ForgotPasswordPage'
import ResetPasswordPage from '@/pages/auth/ResetPasswordPage'
import DashboardPage from '@/pages/dashboard/DashboardPage'
import EarningsPage from '@/pages/dashboard/EarningsPage'
import ReviewsPage from '@/pages/dashboard/ReviewsPage'
import PrivacySecurityPage from '@/pages/dashboard/PrivacySecurityPage'
import MySuccessStoryPage from '@/pages/dashboard/MySuccessStoryPage'
import ProfilePage from '@/pages/profile/ProfilePage'
import EditProfilePage from '@/pages/profile/EditProfilePage'
import MessagesPage from '@/pages/messages/MessagesPage'
import ApplicationsPage from '@/pages/applications/ApplicationsPage'
import ApplicationDetailsPage from '@/pages/applications/ApplicationDetailsPage'
import FavoritesPage from '@/pages/favorites/FavoritesPage'
import SettingsPage from '@/pages/settings/SettingsPage'
import NotificationsPage from '@/pages/notifications/NotificationsPage'
import MyGigsPage from '@/pages/gigs/MyGigsPage'
import VerificationPage from '@/pages/verification/VerificationPage'
import { SubscriptionPage } from '@/pages/subscription/SubscriptionPage'
import { PaymentCallbackPage } from '@/pages/subscription/PaymentCallbackPage'
import WalletPage from '@/pages/wallet/WalletPage'
import TransactionsPage from '@/pages/wallet/TransactionsPage'
import WithdrawalsPage from '@/pages/wallet/WithdrawalsPage'
import WalletSettingsPage from '@/pages/wallet/WalletSettingsPage'

// Admin components
import VerificationQueue from '@/components/verification/VerificationQueue'

// Info pages
import HowItWorksPage from '@/pages/info/HowItWorksPage'
import CategoriesPage from '@/pages/info/CategoriesPage'
import SuccessStoriesPage from '@/pages/info/SuccessStoriesPage'
import SuccessStoryDetailPage from '@/pages/info/SuccessStoryDetailPage'
import SubmitSuccessStoryPage from '@/pages/info/SubmitSuccessStoryPage'
import EditSuccessStoryPage from '@/pages/info/EditSuccessStoryPage'
import BlogPage from '@/pages/info/BlogPage'
import BlogDetailPage from '@/pages/info/BlogDetailPage'
import BlogCategoryPage from '@/pages/info/BlogCategoryPage'
import BlogTagPage from '@/pages/info/BlogTagPage'

// Support pages
import HelpCenterPage from '@/pages/support/HelpCenterPage'
import FAQPage from '@/pages/support/FAQPage'
import ContactPage from '@/pages/support/ContactPage'
// import SafetyPage from '@/pages/support/SafetyPage'
// import DisputePage from '@/pages/support/DisputePage'

// Legal pages
import PrivacyPolicyPage from '@/pages/legal/PrivacyPolicyPage'
import TermsOfServicePage from '@/pages/legal/TermsOfServicePage'
import CookiePolicyPage from '@/pages/legal/CookiePolicyPage'

// Error Boundary
import ErrorBoundary from '@/components/error/ErrorBoundary'
import CookieConsentBanner from '@/components/ui/CookieConsentBanner'

function App() {
    return (
        <ErrorBoundary>
            <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900">
                <Suspense fallback={<LoadingSpinner />}>
                    <Routes>
                        {/* Public Routes */}
                        <Route path="/" element={<Layout />}>
                            <Route index element={<HomePage />} />
                            <Route path="gigs" element={<GigsPage />} />
                            <Route path="gigs/:id" element={<GigDetailsPage />} />

                            {/* Info Pages */}
                            <Route path="how-it-works" element={<HowItWorksPage />} />
                            <Route path="categories" element={<CategoriesPage />} />
                            <Route path="success-stories" element={<SuccessStoriesPage />} />
                            <Route path="success-stories/submit" element={<SubmitSuccessStoryPage />} />
                            <Route path="success-stories/:id/edit" element={<EditSuccessStoryPage />} />
                            <Route path="success-stories/:id" element={<SuccessStoryDetailPage />} />
                            <Route path="blog" element={<BlogPage />} />
                            <Route path="blog/category/:category" element={<BlogCategoryPage />} />
                            <Route path="blog/tag/:tag" element={<BlogTagPage />} />
                            <Route path="blog/:slug" element={<BlogDetailPage />} />

                            {/* Support Pages */}
                            <Route path="help" element={<HelpCenterPage />} />
                            <Route path="faq" element={<FAQPage />} />
                            <Route path="contact" element={<ContactPage />} />
                            {/* <Route path="safety" element={<SafetyPage />} />
                            <Route path="dispute" element={<DisputePage />} /> */}

                            {/* Legal Pages */}
                            <Route path="privacy" element={<PrivacyPolicyPage />} />
                            <Route path="terms" element={<TermsOfServicePage />} />
                            <Route path="cookies" element={<CookiePolicyPage />} />
                        </Route>

                        {/* Auth Routes */}
                        <Route path="/login" element={<LoginPage />} />
                        <Route path="/register" element={<RegisterPage />} />
                        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                        <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
                        <Route path="/verify-email" element={<VerifyEmailPage />} />

                        {/* Subscription Routes */}
                        <Route
                            path="/subscription"
                            element={
                                <ProtectedRoute>
                                    <Layout />
                                </ProtectedRoute>
                            }
                        >
                            <Route index element={<SubscriptionPage />} />
                        </Route>
                        <Route path="/subscription/callback" element={<PaymentCallbackPage />} />

                        {/* Direct Messages Route - Redirects to Dashboard */}
                        <Route
                            path="/messages"
                            element={
                                <ProtectedRoute>
                                    <Layout />
                                </ProtectedRoute>
                            }
                        >
                            <Route index element={<MessagesPage />} />
                        </Route>

                        {/* Protected Routes */}
                        <Route
                            path="/dashboard"
                            element={
                                <ProtectedRoute>
                                    <Layout />
                                </ProtectedRoute>
                            }
                        >
                            <Route index element={<DashboardPage />} />
                            <Route path="profile" element={<ProfilePage />} />
                            <Route path="profile/edit" element={<EditProfilePage />} />
                            <Route path="earnings" element={<EarningsPage />} />
                            <Route path="reviews" element={<ReviewsPage />} />
                            <Route path="security" element={<PrivacySecurityPage />} />
                            <Route path="success-story" element={<MySuccessStoryPage />} />
                            <Route path="messages" element={<MessagesPage />} />
                            <Route path="messages/:conversationId" element={<MessagesPage />} />
                            <Route path="applications" element={<ApplicationsPage />} />
                            <Route path="applications/:id" element={<ApplicationDetailsPage />} />
                            <Route path="favorites" element={<FavoritesPage />} />
                            <Route path="notifications" element={<NotificationsPage />} />
                            <Route path="settings" element={<SettingsPage />} />
                            <Route path="verification" element={<VerificationPage />} />
                            
                            {/* Wallet Routes */}
                            <Route path="wallet" element={<WalletPage />} />
                            <Route path="wallet/transactions" element={<TransactionsPage />} />
                            <Route path="wallet/withdrawals" element={<WithdrawalsPage />} />
                            <Route path="wallet/settings" element={<WalletSettingsPage />} />

                            {/* Employer Routes */}
                            <Route path="gigs/create" element={<CreateGigPage />} />
                            <Route path="gigs/:id/edit" element={<EditGigPage />} />
                            <Route path="my-gigs" element={<MyGigsPage />} />
                        </Route>

                        {/* Public Profile Routes */}
                        <Route path="/profile/:id" element={<Layout />}>
                            <Route index element={<ProfilePage />} />
                        </Route>

                        {/* Admin Routes */}
                        <Route
                            path="/admin"
                            element={
                                <ProtectedRoute>
                                    <AdminLayout />
                                </ProtectedRoute>
                            }
                        >
                            <Route path="verification" element={<VerificationQueue />} />
                        </Route>

                        {/* 404 Route */}
                        <Route path="*" element={<NotFoundPage />} />
                    </Routes>
                </Suspense>
                <CookieConsentBanner />
            </div>
        </ErrorBoundary>
    )
}

export default App